import { Mail, Shield, Award, Calendar, Edit3, Globe, Gavel, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Avatar } from '../../components/common/Avatar';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';

export const Profile = () => {
  const { user } = useAuth();

  return (
    <div className="space-y-8">
      <header className="relative">
        <div className="h-48 w-full rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600" />
        <div className="absolute -bottom-12 left-8 flex items-end space-x-6">
          <Avatar
            src={user?.avatarUrl}
            fallback={user?.username || 'U'}
            size="xl"
            className="h-32 w-32 border-4 border-white shadow-lg"
          />
          <div className="mb-4 space-y-1">
            <h1 className="text-3xl font-bold text-slate-900">{user?.username}</h1>
            <p className="text-slate-500">Keskedo Pioneer • Joined April 2024</p>
          </div>
        </div>
        <div className="absolute -bottom-12 right-8 mb-4">
          <Button variant="outline">
            <Edit3 className="mr-2 h-4 w-4" />
            Edit Profile
          </Button>
        </div>
      </header>

      <div className="mt-16 grid gap-8 lg:grid-cols-3">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>About</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Passionate about decentralized governance and building sustainable digital ecosystems.
                Always looking for new ways to connect and collaborate.
              </p>
              <div className="space-y-3">
                <div className="flex items-center text-sm text-slate-600">
                  <Mail className="mr-3 h-4 w-4" />
                  {user?.email}
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <Globe className="mr-3 h-4 w-4" />
                  keskedo.com/pioneer
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <Calendar className="mr-3 h-4 w-4" />
                  Joined 2 years ago
                </div>
              </div>
              <div className="flex space-x-3 pt-2">
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Reputation & Badges</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                    <Award size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{user?.reputation}</p>
                    <p className="text-xs text-slate-500">Total Reputation</p>
                  </div>
                </div>
                <Badge variant="success">Top 5%</Badge>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="aspect-square rounded-lg bg-slate-50 flex items-center justify-center border border-slate-100">
                    <Shield className="h-6 w-6 text-indigo-400" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Recent Activity</CardTitle>
                <div className="flex space-x-2">
                  <Badge variant="primary" className="cursor-pointer">All</Badge>
                  <Badge variant="secondary" className="cursor-pointer">Governance</Badge>
                  <Badge variant="secondary" className="cursor-pointer">Social</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex space-x-4">
                  <div className="flex flex-col items-center">
                    <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
                      {i % 2 === 0 ? <Gavel size={18} /> : <Users size={18} />}
                    </div>
                    {i < 4 && <div className="w-px flex-1 bg-slate-100 my-2" />}
                  </div>
                  <div className="pb-8">
                    <p className="text-sm text-slate-900 font-medium">
                      {i % 2 === 0
                        ? 'Voted on Proposal #124: Ecosystem Fee Structure'
                        : 'Joined the "Web3 Builders" Circle'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {i === 1 ? '2 hours ago' : i === 2 ? 'Yesterday' : `${i} days ago`}
                    </p>
                    <div className="mt-3 rounded-xl border border-slate-100 p-4 bg-slate-50/50">
                      <p className="text-sm text-slate-600">
                        {i % 2 === 0
                          ? 'Voted "FOR" with 750 Voting Power. This proposal is critical for new developers.'
                          : 'Excited to collaborate with other builders in this space!'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
